import { Target, Layers, Users2, Sparkles } from "lucide-react";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0ZWNoJTIwdGVhbSUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3ODM2NzU5MzJ8MA&ixlib=rb-4.1.0&q=85";

const values = [
  {
    icon: Target,
    title: "Outcomes, not outputs",
    body: "We measure ourselves on business KPIs — cost, cycle-time, revenue — not on models trained or dashboards shipped.",
  },
  {
    icon: Layers,
    title: "Deep + Broad",
    body: "Each partner brings a decade in one vertical, then multiplies it with cross-domain patterns. Rare in a market of specialists.",
  },
  {
    icon: Users2,
    title: "Embedded, not vendor",
    body: "We work as one of your teams — in your Slack, your stand-ups, your reviews — until knowledge transfer is complete.",
  },
  {
    icon: Sparkles,
    title: "Research → Product",
    body: "Our applied research squad publishes, prototypes and productionises — closing the gap between paper and P&L.",
  },
];

export default function About() {
  return (
    <main data-testid="page-about" className="pt-24">
      <section className="relative pt-16 pb-16">
        <div className="container-x">
          <div className="font-mono-label text-xs text-[#00F0FF] mb-4">
            / About Pransam
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight max-w-4xl leading-[1.02]">
            A studio for teams that treat{" "}
            <span className="text-[#00F0FF]">AI as engineering</span>, not
            magic.
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">
            Pransam Technologies LLP was founded on a simple premise:
            organisations don&apos;t need more AI demos — they need production
            systems that hold up on a Tuesday morning. We&apos;re a compact team of
            engineers, researchers and industry veterans, structured around
            four vertical practices and one shared research spine.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x">
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <img
              src={ABOUT_IMG}
              alt="Pransam team"
              className="w-full h-[420px] md:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#050505]/80 via-transparent to-[#050505]/20" />
            <div className="absolute bottom-6 left-6 right-6 md:bottom-10 md:left-10 md:right-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div className="font-display text-2xl md:text-3xl text-white max-w-lg tracking-tight">
                Bengaluru-headquartered. Working with teams across India, EU
                and North America.
              </div>
              <div className="font-mono-label text-[11px] text-[#00F0FF]">
                EST. 2024
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="container-x">
          <div className="font-mono-label text-xs text-[#00F0FF] mb-4">
            / How we work
          </div>
          <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight max-w-3xl">
            Four principles that shape every engagement.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`value-card-${i}`}
                  className="rounded-2xl border border-white/10 bg-[#0F0F13] p-8 transition-transform duration-300 hover:-translate-y-1 hover:border-[#00F0FF]/40"
                >
                  <div className="w-12 h-12 rounded-xl grid place-items-center bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF]">
                    <Icon size={22} strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-6 font-display text-2xl text-white tracking-tight">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-white/60 leading-relaxed">
                    {v.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <div className="container-x relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { k: "10+ yrs", v: "Avg. engineer experience" },
              { k: "4", v: "Vertical practices" },
              { k: "1", v: "Shared research spine" },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`about-stat-${i}`}
                className="border-l border-[#00F0FF]/40 pl-6"
              >
                <div className="font-display text-4xl text-white">{s.k}</div>
                <div className="font-mono-label text-[10px] text-white/50 mt-2">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
